import { useEffect, useState } from 'react';

import { useSidebarCardLadder } from './useSidebarCardLadder';

// The sidebar animates between 68px and 260px over 200ms (AppSidebar's
// `transition-all duration-200`). A card that paints mid-animation fades in at a
// transitional width, so an expand waits the animation out with a little slack.
const EXPAND_TRANSITION_MS = 300;

// Which cards have reported an impression this page load. Module scope rather
// than a ref on purpose: MobileNavDrawer renders its own AppSidebar inside a
// Transition that unmounts on close, so a per-instance ref would report a fresh
// impression every time a teacher peeks at the drawer. That is the opposite skew
// from the one isVisibleSurface prevents, and it would make mobile count per
// peek while desktop counts once for a card sitting continuously on screen. The
// in-resource slot solved the same problem the same way (ResourceAd.jsx).
let reportedShownKeys = new Set();

// Module state outlives an individual test — vitest isolates modules per file,
// not per test.
export const resetReportedImpressionsForTest = () => {
  reportedShownKeys = new Set();
};

// True once the card is safe to paint. Seeded from `isExpanded` so a slot that
// mounts into an already-open sidebar paints immediately — there is no animation
// to wait for there, and delaying would put a visible hole in the sidebar on
// every navigation. Only a collapsed -> expanded transition actually waits.
function useExpandTransitionSettled(isExpanded) {
  const [isSettled, setIsSettled] = useState(isExpanded);

  useEffect(() => {
    if (!isExpanded) {
      setIsSettled(false);
      return undefined;
    }
    const timer = setTimeout(() => setIsSettled(true), EXPAND_TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [isExpanded]);

  return isSettled;
}

/**
 * The single owner of the card slot pinned above the profile row in AppSidebar.
 *
 * Everything about WHICH card shows lives in useSidebarCardLadder; this owns the
 * four rules that hold whatever the ladder contains, so a new contender cannot
 * forget them:
 *
 *   1. exactly one card is ever on screen — first eligible in the ladder wins,
 *   2. nothing renders on the collapsed 68px rail,
 *   3. an expand waits out the width animation before painting,
 *   4. the slot stays empty while a contender that could still WIN is undecided.
 *
 * A fifth thing it owns, for the contenders that care: telling the winner it was
 * actually SEEN. AppNavigation mounts two AppSidebars and hides the desktop one
 * with CSS, so it stays mounted — and its effects keep running — at every
 * viewport. A card counting its own impression on mount would count one on every
 * mobile session, invisibly. `isVisibleSurface` is how the slot knows the
 * difference, and `onShown` fires once per winner per page load.
 *
 * Rule 4 is the one that is easy to lose. A contender may resolve
 * asynchronously — an ad's targeting can cost a round trip — and without the
 * barrier a lower-priority card paints first and is then yanked away, which is
 * the welcome-card flash this slot has already been fixed for once. It waits
 * only on contenders ranked ABOVE the first eligible card, though: a decision
 * that cannot change the outcome must not hold the slot blank.
 */
export function SidebarSlot({ isExpanded, isVisibleSurface = true }) {
  const ladder = useSidebarCardLadder();
  const isTransitionSettled = useExpandTransitionSettled(isExpanded);

  // Block only on contenders that could still WIN. Waiting for every rung means
  // a card ranked above an unresolved one is held back by a decision that cannot
  // affect it — on /classic, the classic notice would sit blank for the length of
  // the ad rung's network call. The first contender that is either eligible or
  // still deciding is the one that settles the slot.
  const decisive =
    isExpanded && isTransitionSettled
      ? ladder.find((card) => !card.isResolved || card.isEligible)
      : undefined;
  const winner = decisive?.isResolved ? decisive : undefined;

  // Keyed on the winner's own impression key where it has one, so a rung whose
  // id is a constant can still distinguish the thing it is showing.
  const shownId = winner && isVisibleSurface ? (winner.shownKey ?? winner.id) : null;

  // Once per winner per page load, across mounts — see reportedShownKeys.
  useEffect(() => {
    if (!shownId || reportedShownKeys.has(shownId)) return;
    reportedShownKeys.add(shownId);
    winner?.onShown?.();
  }, [shownId]);

  return winner ? winner.render() : null;
}

export default SidebarSlot;
